import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Upload } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { invalidatePriorityQueue, useRefreshCurrentUser } from '~/api';
import { Tabs, type TabItem } from '~/components/ui/Tabs';
import { Button } from '~/components/ui';
import { Modal } from '~/components/overlays/Modal';
import { canEditOrderNotifications } from './orderNotificationsAccess';
import { OrderNotificationsSection } from './sections/OrderNotificationsSection';
import { OrganizationAddresses } from './sections/OrganizationAddresses';
import { OrganizationLocations } from './sections/OrganizationLocations';
import { TradingPartners } from './sections/TradingPartners';
import { AddInventoryModal } from './sections/AddInventoryModal';
import { BuyerInventoryUploadCsv } from './sections/BuyerInventoryUploadCsv';
import { UnitPreferences } from '~/pages/user-preferences/sections/UnitPreferences';

export function Settings() {
  const { t } = useTranslation('settings');
  // Reads role straight from the query cache rather than Redux: Redux is populated by an effect
  // in useRefreshCurrentUser one render after this data resolves, and Tabs' unmatched-path
  // fallback runs synchronously during render — with the Redux-sourced role it would see a
  // transient "no role yet" tab list and redirect away from a role-specific tab before Redux
  // catches up (e.g. a seller reloading /settings/buyers would bounce to /settings/locations).
  const { data } = useRefreshCurrentUser();
  const role = data?.currentUser?.role;
  const queryClient = useQueryClient();
  const [showAddInventory, setShowAddInventory] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);

  const tabs: TabItem[] = [
    {
      label: t('settings.locations.tab'),
      link: 'locations',
      component: <OrganizationLocations />,
    },
    {
      label: t('settings.facilities.tab'),
      link: 'addresses',
      component: <OrganizationAddresses />,
    },
  ];

  if (canEditOrderNotifications(role)) {
    tabs.push({
      label: t('settings.order_notifications.tab'),
      link: 'order-notifications',
      component: (
        <OrderNotificationsSection
          saved={
            data?.currentUser?.organization?.orderNotificationEmails ?? null
          }
        />
      ),
    });
  }

  if (role === 'BUYER') {
    tabs.push({
      label: t('settings.trading_partners.sellers_tab'),
      link: 'sellers',
      component: <TradingPartners variant="sellers" />,
    });
  } else if (role === 'SELLER') {
    tabs.push({
      label: t('settings.trading_partners.buyers_tab'),
      link: 'buyers',
      component: <TradingPartners variant="buyers" />,
    });
  }

  tabs.push({
    label: t('settings.user_settings.tab'),
    link: 'user-settings',
    component: <UnitPreferences />,
  });

  // The upsert hooks already refresh the flat list; also refresh the priority
  // queue so a newly added facility x product group shows up without a manual reload.
  const handleInventoryAdded = () => {
    invalidatePriorityQueue(queryClient);
  };

  const headerActions =
    role === 'BUYER' ? (
      <div className="flex items-center gap-3">
        <Button
          variant="accent"
          size="sm"
          width="auto"
          onClick={() => setShowUploadModal(true)}
        >
          <Upload size={16} />
          {t('settings.upload_inventory')}
        </Button>
        <Button
          variant="primary"
          size="sm"
          width="auto"
          onClick={() => setShowAddInventory(true)}
        >
          <Plus size={16} />
          {t('settings.add_inventory')}
        </Button>
        <Modal
          isOpen={showAddInventory}
          onClose={() => setShowAddInventory(false)}
        >
          <AddInventoryModal
            setShowAddInventory={setShowAddInventory}
            onSaved={handleInventoryAdded}
          />
        </Modal>
        <BuyerInventoryUploadCsv
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          withDataEnabled={false}
        />
      </div>
    ) : null;

  return (
    <Tabs
      title={t('settings.title')}
      tabs={tabs}
      basePath="settings"
      headerActions={headerActions}
    />
  );
}
